import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { getAllMasterData } from "helpers/backend_helper";
import { mapToOptions } from "utils/optionMapper";

const MasterDataContext = createContext();
const CACHE_KEY = "masterDataCache";

// Default structure
const defaultMasterData = {
  academics: [],
  clusters: [],
  programs: [],
  centers: [],
  categories: [],
  payments: [],
  roles: [],
  currentAcademic: {value:'',id:'',label:'',academicYear:'',currentAY:''},
};

export const MasterDataProvider = ({ children }) => {
  const [masterData, setMasterData] = useState(defaultMasterData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch master data from backend (only if cache not available)
   */
  const fetchMasterData = useCallback(async () => {
    try {
      setLoading(true);

      //  Check cache first
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        setMasterData(parsed.data);
        // console.log("Loaded master data from cache ");
        return parsed.data;
      }

      //  No cache → fetch from API
      const res = await getAllMasterData();
      const mastersData = res?.data?.data || {};

      const mappedData = {
        academics: mapToOptions(mastersData.academics, "id", "academicYear"),
        clusters: mapToOptions(
          mastersData.clusters,
          "cluster_id",
          "cluster_name"
        ),
        programs: mapToOptions(
          mastersData.programs,
          "program_id",
          "program_name"
        ),
        centers: mapToOptions(
          mastersData.centers,
          "center_id",
          "center_name"
        ),
        categories: mapToOptions(
          mastersData.categories,
          "category_id",
          "category_name"
        ),
        payments: mapToOptions(mastersData.payments, "id", "payment_name"),
        roles: mapToOptions(mastersData.roles, "role_id", "roleName"),
        currentAcademic:
          mastersData.academics?.find((ay) => ay.currentAY === 1) || null,
      };

      // ✅ Save to state + cache only when API data is ready
      setMasterData(mappedData);
      localStorage.setItem(CACHE_KEY, JSON.stringify({ data: mappedData }));

      // console.log("Fetched master data from API 🔄");
      return mappedData;
    } catch (err) {
      console.warn("Error fetching master data:", err);
      setError(err.message || "Failed to fetch master data");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * On mount: if token exists, use cache or fetch API
   */
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (token) {
      const cached = localStorage.getItem(CACHE_KEY);

      if (cached) {
        const parsed = JSON.parse(cached);
        setMasterData(parsed.data);
        // console.log("Loaded master data from cache ✅");
      } else {
        fetchMasterData();
      }
    } else {
      // logout case
      setMasterData(defaultMasterData);
      localStorage.removeItem(CACHE_KEY);
    }
  }, [fetchMasterData]);

 const updateMasterData = useCallback(
  (key, value, action = "replace", idKey = "id") => {
    setMasterData((prev) => {
      let updatedSection = Array.isArray(prev[key]) ? [...prev[key]] : [];

      switch (action.toLowerCase()) {
        case "add": {
          if (!value || !value[idKey]) {
            console.warn(`updateMasterData(add): Missing ${idKey} in value`, value);
            break;
          }

          const index = updatedSection.findIndex((item) => item[idKey] === value[idKey]);
          if (index > -1) {
            // ✅ Update existing
            updatedSection[index] = { ...updatedSection[index], ...value };
          } else {
            // ✅ Add new
            updatedSection.push(value);
          }
          break;
        }

        case "update": {
          if (!value || !value[idKey]) {
            console.warn(`updateMasterData(update): Missing ${idKey} in value`, value);
            break;
          }

          const index = updatedSection.findIndex((item) => item[idKey] === value[idKey]);
          if (index > -1) {
            // ✅ Update only if exists
            updatedSection[index] = { ...updatedSection[index], ...value };
          } else {
            console.warn(
              `updateMasterData(update): Item with ${idKey}=${value[idKey]} not found in '${key}'`
            );
          }
          break;
        }

        case "delete": {
          if (!value || !value[idKey]) {
            console.warn(`updateMasterData(delete): Missing ${idKey} in value`, value);
            break;
          }

          updatedSection = updatedSection.filter((item) => item[idKey] !== value[idKey]);
          break;
        }

        case "replace":
        default:
          updatedSection = value;
          break;
      }

      const updated = { ...prev, [key]: updatedSection };
      localStorage.setItem(CACHE_KEY, JSON.stringify({ data: updated }));
      return updated;
    });
  },
  []
);



  /**
   * Clear master data (for logout)
   */
  const clearMasterData = useCallback(() => {
    setMasterData(defaultMasterData);
    localStorage.removeItem(CACHE_KEY);
  }, []);

  return (
    <MasterDataContext.Provider
      value={{
        ...masterData,
        loading,
        error,
        fetchMasterData,   // manual refresh
        updateMasterData,  // replace array/object + cache update
        clearMasterData,   // use in logout
      }}
    >
      {children}
    </MasterDataContext.Provider>
  );
};

/**
 * Custom hook for consuming master data
 */
export const useMasterData = () => useContext(MasterDataContext);
